import type { ApexOptions } from 'apexcharts';
import { TFunction } from 'i18next';
import { ColorEnum } from 'graphql-common';
import { getApexchartsLocales } from '@lib/utils/apexcharts';
// Re-use the Monitoring tooltip styles so analytics charts share the same
// visual language across the admin app.
import tooltipStyles from 'routes/Dashboard/Monitoring/MonitoringChartTooltip/MonitoringChartTooltip.module.scss';
import { CategoryAnalyticsRow } from './getCategoryTableColumns';

const FALLBACK_PALETTE = [
  '#324DDA',
  '#7C3AED',
  '#1AAB6F',
  '#A8D45F',
  '#F4B83A',
  '#F37C2A',
  '#E13F58',
  '#E83CAA',
  '#7B5E2F',
  '#9CA0B0',
];

// `Documentation.category.color` is a `ColorEnum` (e.g. "BLUE"), not a hex value,
// so mirror the values defined in `lib/src/assets/styles/_scssColors` to render
// real category colors in the pie chart.
const COLOR_ENUM_HEX: Record<ColorEnum, string> = {
  [ColorEnum.Aquamarine]: '#00ABB6',
  [ColorEnum.Black]: '#414458',
  [ColorEnum.Blue]: '#214EEB',
  [ColorEnum.Brown]: '#5B3905',
  [ColorEnum.Coral]: '#FF551F',
  [ColorEnum.Gray]: '#9C9EB6',
  [ColorEnum.Green]: '#00B67A',
  [ColorEnum.Lime]: '#7FE300',
  [ColorEnum.Orange]: '#FFA51F',
  [ColorEnum.Pink]: '#EB21E3',
  [ColorEnum.Red]: '#EB214D',
  [ColorEnum.Violet]: '#8621EB',
  [ColorEnum.White]: '#D6D8F0',
  [ColorEnum.Yellow]: '#FFE500',
};

function resolveCategoryHex(
  color: string | null | undefined,
  fallback: string,
): string {
  if (!color) return fallback;
  // Already a hex / rgb / css color literal — pass through.
  if (color.startsWith('#') || color.startsWith('rgb')) return color;
  // Map the `ColorEnum` to its hex; unknown values fall back to the palette so
  // distinct chart slices stay visually distinguishable.
  return COLOR_ENUM_HEX[color.toUpperCase() as ColorEnum] || fallback;
}

interface BuildArgs {
  rows: CategoryAnalyticsRow[];
  useRealColors: boolean;
  locale: string;
  t: TFunction<'translation', undefined>;
}

export default function buildCategoriesPieData({
  rows,
  useRealColors,
  locale,
  t,
}: BuildArgs): { options: ApexOptions; series: number[] } {
  const series = rows.map((r) => r.viewCount);
  const labels = rows.map((r) => r.name);

  const colors = useRealColors
    ? rows.map((r, idx) =>
        resolveCategoryHex(
          r.color,
          FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length],
        ),
      )
    : rows.map((_, idx) => FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length]);

  const options: ApexOptions = {
    chart: {
      type: 'donut',
      ...getApexchartsLocales(locale),
    },
    labels,
    colors,
    legend: {
      show: false,
    },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '55%',
        },
      },
    },
    tooltip: {
      // Mirror MonitoringChartTooltip: title row at the top (category name)
      // followed by a single tooltipRow with a colored indicator + the value.
      custom: ({ seriesIndex, w }) => {
        const label =
          rows[seriesIndex]?.name ??
          (w?.globals?.labels?.[seriesIndex] as string | undefined) ??
          '';
        const value = w?.globals?.series?.[seriesIndex] ?? 0;
        const pct = rows[seriesIndex]?.percentage ?? 0;
        const color =
          (w?.globals?.colors?.[seriesIndex] as string | undefined) ??
          'transparent';
        const escape = (raw: string) =>
          raw
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        return [
          `<div class="${tooltipStyles.tooltip}">`,
          `<div class="${tooltipStyles.title}">${escape(String(label))}</div>`,
          `<div class="${tooltipStyles.list}">`,
          `<div class="${tooltipStyles.tooltipRow}">`,
          `<div class="${tooltipStyles.colorIndicator}" style="background-color: ${color};"></div>`,
          `<span class="${tooltipStyles.value}">${Math.round(pct)}% (${value})</span>`,
          '</div>',
          '</div>',
          '</div>',
        ].join('');
      },
    },
    noData: { text: t('no-results-found') },
  };

  return { options, series };
}
